/**
 * Der Verlauf räumt sich selbst auf. (22.09.2026)
 *
 * ## Woher das kommt
 *
 * Martin: *„Ich möchte vermerken, dass der Mistkübel verschwinden darf …
 * Ich möchte, dass ein Auto-Archiv aktiviert wird, da ich jetzt schon seit
 * Wochen nicht wirklich mehr den Mistkübel verwende. Der Chatverlauf soll
 * dann einfach nach einer gewissen Anzahl von Tagen ins Archiv verschwinden,
 * weil die User in der Regel gar nicht scrollen."*
 *
 * ## Warum das kein nächtlicher Lauf ist
 *
 * Was „Chat leeren" seit Pre.211 tut, ist NICHT löschen: es setzt einen
 * Stichtag (`users.chat_cutoff_at`). Alles davor liegt im Archiv und ist
 * jederzeit wieder da. Ein Auto-Archiv muss also nichts wegräumen — es muss
 * nur den Stichtag mitwandern lassen.
 *
 * Und genau das lässt sich BEIM LESEN rechnen, statt es irgendwohin zu
 * schreiben. Das hat drei Folgen, die alle zählen:
 *
 *   - Es gibt keinen Job, der ausfallen kann. (Der Prüfstand-Nachtlauf ist
 *     heute früh am Wochenkontingent gescheitert — ein Job weniger ist ein
 *     Ausfall weniger.)
 *   - Es wird nichts geschrieben, also kann nichts kaputtgehen.
 *   - Die Einstellung auf „aus" zu stellen holt den ganzen Verlauf im selben
 *     Moment zurück. Eine Reue-Taste, die keine sein muss.
 *
 * ⚠️ Der HANDgesetzte Stichtag gewinnt immer, wenn er jünger ist. Wer
 *    gerade selbst archiviert hat, will nicht, dass ihm die Automatik den
 *    Verlauf wieder hervorholt.
 */

/** Aus wie vielen Tagen man wählen kann. 0 heisst: Auto-Archiv aus. */
export const ARCHIV_TAGE_AUSWAHL: readonly number[] = [0, 7, 14, 30, 90];

/**
 * Nach so vielen Tagen wandert der Verlauf von selbst ins Archiv.
 *
 * ⚠️ Dreissig Tage sind kein Gefühl, sondern die kleinste Zahl, bei der ein
 *    Monatsrückblick noch vollständig im sichtbaren Verlauf steht. Kürzer
 *    schneidet mitten in einen Zusammenhang, den jemand noch sucht.
 */
export const ARCHIV_TAGE_STANDARD = 30;

/** Die Einstellung auf einen erlaubten Wert bringen. */
export function tageBereinigen(roh: unknown): number {
  const n = typeof roh === "number" ? roh : Number(roh);
  if (!Number.isFinite(n) || n <= 0) return 0;
  const gerundet = Math.round(n);
  // Ein Wert zwischen den Stufen ist kein Fehler — er wird einfach gehalten,
  // solange er im vernuenftigen Rahmen liegt.
  return Math.min(3650, Math.max(1, gerundet));
}

/**
 * Welcher Stichtag gilt wirklich?
 *
 * @param handSchnitt  Der selbst gesetzte Stichtag (ISO) oder null.
 * @param tage         Auto-Archiv nach so vielen Tagen; 0 = aus.
 * @param jetzt        Bezugszeitpunkt.
 * @returns ISO-Zeitstempel oder null, wenn gar nichts archiviert ist.
 */
export function wirksamerSchnitt(
  handSchnitt: string | null | undefined,
  tage: number,
  jetzt: Date = new Date(),
): string | null {
  const t = tageBereinigen(tage);
  const auto =
    t > 0 ? new Date(jetzt.getTime() - t * 86_400_000).toISOString() : null;
  const hand = handSchnitt?.trim() ? normalisiere(handSchnitt) : null;
  if (!auto) return hand;
  if (!hand) return auto;
  /*
   * ⚠️ Der SPAETERE gewinnt. Wer heute von Hand archiviert hat, dessen
   *    Stichtag liegt vor wenigen Minuten — der Auto-Stichtag von vor
   *    dreissig Tagen wuerde den Verlauf wieder hervorholen.
   */
  return hand > auto ? hand : auto;
}

/**
 * Zeitstempel vergleichbar machen.
 *
 * ⚠️ Die Stempel stehen naiv in UTC (ohne Z). Ohne das angehaengte Z liest
 *    JavaScript sie als Ortszeit — bei zwei Stunden Versatz waere jeder
 *    Handschnitt „aus der Zukunft" und gewaenne immer.
 */
function normalisiere(roh: string): string {
  const hatZone = /[Zz]|[+-]\d\d:?\d\d$/.test(roh);
  const iso = hatZone ? roh.replace(" ", "T") : roh.replace(" ", "T") + "Z";
  const t = Date.parse(iso);
  return Number.isFinite(t) ? new Date(t).toISOString() : roh;
}

/** Ein Satz für die Einstellungen — was gerade gilt, in Worten. */
export function archivSatz(tage: number): string {
  const t = tageBereinigen(tage);
  if (t === 0) {
    return "Der Verlauf bleibt vollständig stehen, bis du selbst archivierst.";
  }
  return (
    `Alles, was älter als ${t} Tage ist, liegt im Archiv — sichtbar bleibt ` +
    `die letzte Zeit. Nichts wird gelöscht: im Archiv ist jede Nachricht ` +
    `weiter da, und wenn du das hier ausschaltest, ist der ganze Verlauf ` +
    `sofort wieder zurück.`
  );
}

/*
 * ## „Verlauf kürzen“ — die letzten N bleiben (02.10.2026, pre.3603)
 *
 * Der Knopf in der Seitenleiste setzt denselben Stichtag wie „Chat leeren“
 * früher — nichts wird gelöscht, das Archiv bleibt abrufbar. Gezählt werden
 * NACHRICHTEN (Zeilen in chat_messages: eine Frage, eine Antwort, eine
 * Systemmeldung = je eine Blase). Werkzeugkarten hängen an ihrer Antwort und
 * gehen mit ihr.
 *
 * ⚠️ Der Verlauf vergleicht sekundengenau (`datetime(created_at) > datetime(stichtag)`).
 *    Stand der Stichtag auf der ersten AUSGEBLENDETEN Nachricht und lag eine
 *    behaltene in derselben Sekunde, verschwand sie mit — es blieben weniger als
 *    versprochen. Darum liegt der Stichtag hier auf der jüngsten Nachricht, die
 *    eine GANZE Sekunde älter ist als die N-te; bei Gleichstand bleiben ein paar
 *    mehr sichtbar, nie weniger.
 */

/** So viele bleiben, wenn der Aufrufer nichts sagt (Fassung vor pre.3603, ältere Oberflächen). */
export const BEHALTEN_STANDARD = 6;
/** Mehr als so viele „letzte“ ist kein Kürzen mehr. */
const BEHALTEN_HOECHSTENS = 50;

/** Die gewünschte Zahl auf einen erlaubten Wert bringen (1 … 50, sonst der Standard). */
export function behaltenBereinigen(roh: unknown): number {
  const n = typeof roh === "number" ? roh : Number(roh);
  if (roh === undefined || roh === null || roh === "" || !Number.isFinite(n) || n < 1) {
    return BEHALTEN_STANDARD;
  }
  return Math.min(BEHALTEN_HOECHSTENS, Math.round(n));
}

/** Sekunde (UTC) eines Stempels aus der Datenbank — naiv-UTC oder ISO. */
function sekunde(roh: string): number | null {
  const t = Date.parse(normalisiere(roh));
  return Number.isFinite(t) ? Math.floor(t / 1000) : null;
}

/**
 * Der Stichtag, nach dem genau die letzten `n` Nachrichten sichtbar bleiben.
 *
 * @param stempel  `created_at` der jüngsten Nachrichten des Kontos (beliebige Reihenfolge;
 *                 der Aufrufer holt ein paar mehr als `n`, damit Gleichstände aufgehen).
 * @returns ISO-Zeitstempel und wie viele danach sichtbar sind — oder null, wenn es nichts
 *          auszublenden gibt (höchstens `n` Nachrichten, oder alle in derselben Sekunde).
 */
export function schnittVorDenLetzten(
  stempel: readonly string[],
  n: number,
): { stichtag: string; sichtbar: number } | null {
  const sek = stempel
    .map(sekunde)
    .filter((s): s is number => s !== null)
    .sort((a, b) => b - a);
  if (n < 1 || sek.length <= n) return null;
  const nte = sek[n - 1];
  if (nte === undefined) return null;
  const i = sek.findIndex((s, j) => j >= n && s < nte);
  const davor = i < 0 ? undefined : sek[i];
  if (davor === undefined) return null;
  return { stichtag: new Date(davor * 1000).toISOString(), sichtbar: i };
}
